import { Client, Session } from '@heroiclabs/nakama-js';
import type { NakamaConfig } from '../types/nakama';

const AUTH_TOKEN_KEY = 'nakama_auth_token';
const REFRESH_TOKEN_KEY = 'nakama_refresh_token';
const OBFUSCATION_PREFIX = 'v1:';

let client: Client | null = null;
let session: Session | null = null;

// Simple sync obfuscation to prevent plaintext token storage in localStorage.
// Real XSS protection comes from CSP headers — this is defense-in-depth.
function obfuscate(value: string): string {
  // XOR with a repeating key, then base64 encode
  const key = 'aliza-games-token-v1';
  const bytes = new Uint8Array(value.length);
  for (let i = 0; i < value.length; i++) {
    bytes[i] = value.charCodeAt(i) ^ key.charCodeAt(i % key.length);
  }
  return OBFUSCATION_PREFIX + btoa(String.fromCharCode(...bytes));
}

function deobfuscate(stored: string): string {
  // Handle legacy plaintext tokens (no prefix)
  if (!stored.startsWith(OBFUSCATION_PREFIX)) return stored;

  try {
    const base64 = stored.slice(OBFUSCATION_PREFIX.length);
    const decoded = atob(base64);
    const key = 'aliza-games-token-v1';
    const chars: string[] = [];
    for (let i = 0; i < decoded.length; i++) {
      chars.push(String.fromCharCode(decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length)));
    }
    return chars.join('');
  } catch {
    return '';
  }
}

/**
 * Initialize the Nakama client singleton.
 * Must be called before any other API functions.
 */
export function initClient(config: NakamaConfig): void {
  client = new Client(
    config.serverKey,
    config.host,
    config.port,
    config.useSSL,
    config.timeout,
    config.autoRefreshSession,
  );
}

/**
 * Get the initialized Nakama client.
 * Throws if initClient() has not been called.
 */
export function getClient(): Client {
  if (client === null) {
    throw new Error(
      'Nakama client not initialized. Call initClient() first.',
    );
  }
  return client;
}

/**
 * Get the current session, or null if not authenticated.
 */
export function getSession(): Session | null {
  return session;
}

/**
 * Set the current session and persist obfuscated tokens to localStorage.
 */
export function setSession(newSession: Session): void {
  session = newSession;
  localStorage.setItem(AUTH_TOKEN_KEY, obfuscate(newSession.token));
  localStorage.setItem(REFRESH_TOKEN_KEY, obfuscate(newSession.refresh_token));
}

/**
 * Clear the current session and remove tokens from localStorage.
 */
export function clearSession(): void {
  session = null;
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * Restore a session from obfuscated tokens persisted in localStorage.
 * Returns null if no stored tokens are found.
 */
export function restoreSession(): Session | null {
  const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
  const storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);

  if (storedToken === null || storedRefresh === null) {
    return null;
  }

  const token = deobfuscate(storedToken);
  const refreshToken = deobfuscate(storedRefresh);

  if (!token || !refreshToken) {
    clearSession();
    return null;
  }

  const restored = Session.restore(token, refreshToken);
  session = restored;
  return restored;
}

/**
 * Reset internal state. Intended for testing only.
 */
export function _resetForTesting(): void {
  client = null;
  session = null;
}
